// 214 §6 [RULED 52] — A LAWYER'S RECORD LISTS HIS MATTERS. On the Persons list's record of an opposing counsel,
// and of a client's contact whose role is Avocat: every matter he is linked to — regular and collective, the
// archived ones fetched as ClientCasesCard.tsx does (the app's loaded lists exclude them) — with the party he
// represents there (a counsel's parties on the link; a contact's own client) and the link's state: acting, or
// « suspendu depuis le JJ/MM/AA ». The acting ones come first. Opening a row opens the matter. A matter the reader
// may not see is not listed, because the API serves only what he may see (D.8).
//
// Before 214 the record showed the default « Dossiers » card (EntityList.tsx) — ref pills over the loaded regular
// cases: no collective case, no archived matter, no party, no state (docs/214-deploy-note.md §7.6). A stable,
// module-scope component (its per-record fetch/state survive App re-renders); no `any`, no warning.
import React, { useState, useEffect } from "react";
import { api } from "../api";
import { useTranslation } from "../i18n";
import { FS } from "../lib/tokens";
import { pname } from "../utils";
import { orderParties } from "../lib/memberOrder"; // 216 §4 [RULED 71] — a counsel's parties by last name
import type { TFn } from "../lib/taxonomyTypes";
import { actingFirst, ccElementOf, ccLinksOf, counselIdsOf, isSuspended, linkOf, partyIdsOf, shortDate, suspendedDateOf, type CounselLink, type LinkMap, type LinkState, type NavFn } from "../lib/counselLinks";

interface MatterRow {
  readonly id: number;
  readonly ref?: string | null;
  readonly title?: string | null;
  readonly archived?: boolean | null;
  readonly ocuIds?: unknown;
  readonly cpIds?: unknown;
  readonly ocuLinks: LinkMap<CounselLink>;
  readonly cpLinks: LinkMap<LinkState>;
}
interface PersonRow {
  readonly id: number;
  readonly clientId?: number | null;
}
interface NamedRow {
  readonly id?: number | null;
}

interface Props {
  readonly kind: "ocu" | "cp";
  readonly item: PersonRow;
  readonly cases: readonly unknown[];
  readonly collectiveCases: readonly unknown[];
  readonly opponents: readonly unknown[];
  readonly clients: readonly unknown[];
  readonly nav: NavFn;
}

interface Listed {
  readonly key: string;
  readonly matter: MatterRow;
  readonly isCC: boolean;
  readonly party: string;
  readonly link: LinkState | null;
}

const rowsOf = (list: readonly unknown[]): MatterRow[] =>
  list.filter((c): c is MatterRow => typeof c === "object" && c !== null && typeof (c as MatterRow).id === "number");

function mergeById(active: readonly MatterRow[], archived: readonly MatterRow[]): MatterRow[] {
  const seen = new Set(active.map((r) => r.id));
  return [...active, ...archived.filter((r) => !seen.has(r.id))];
}

export default function PartyMattersCard({ kind, item, cases, collectiveCases, opponents, clients, nav }: Props) {
  const { t } = useTranslation() as { t: TFn };
  const [arch, setArch] = useState<{ cases: MatterRow[]; ccs: MatterRow[] }>({ cases: [], ccs: [] });

  useEffect(() => {
    let dead = false;
    const archived = (path: string): Promise<unknown> => api.get(path).catch((): unknown[] => []);
    void Promise.all([archived("/cases?archived=1"), archived("/collective-cases?archived=1")]).then(([acs, accs]) => {
      if (dead) return;
      setArch({ cases: rowsOf(Array.isArray(acs) ? acs : []), ccs: rowsOf(Array.isArray(accs) ? accs : []) });
    });
    return () => {
      dead = true;
    };
  }, [item.id]);

  const nameOf = (pool: readonly unknown[], id: number | null | undefined): string => {
    const row = pool.find((p) => typeof p === "object" && p !== null && (p as NamedRow).id === id);
    return row ? pname(row) : "";
  };
  const linkedIn = (ids: unknown) => counselIdsOf(ids).includes(item.id);
  const regular = mergeById(rowsOf(cases), arch.cases).filter((c) => linkedIn(kind === "ocu" ? c.ocuIds : c.cpIds));
  const collective = mergeById(rowsOf(collectiveCases), arch.ccs).filter((c) => (kind === "ocu" ? !!ccElementOf(c.ocuIds, item.id) : linkedIn(c.cpIds)));

  // 216 §4 [RULED 71] — the parties a counsel acts for, in the one order (the link's stored order until now)
  const rowOf = (pool: readonly unknown[], id: number | null | undefined): NamedRow | null =>
    (pool.find((p) => typeof p === "object" && p !== null && (p as NamedRow).id === id) as NamedRow | undefined) ?? null;
  const partyOfCounsel = (link: CounselLink | null): string =>
    orderParties(partyIdsOf(link).map((id) => rowOf(opponents, id)).filter((r): r is NamedRow => !!r))
      .map((r) => pname(r))
      .filter(Boolean)
      .join(", ");
  const clientName = nameOf(clients, item.clientId);

  const listed: Listed[] = [
    ...regular.map((c): Listed => {
      if (kind === "ocu") {
        const link = linkOf(c.ocuLinks, item.id);
        return { key: "ca" + String(c.id), matter: c, isCC: false, party: partyOfCounsel(link), link };
      }
      return { key: "ca" + String(c.id), matter: c, isCC: false, party: clientName, link: linkOf(c.cpLinks, item.id) };
    }),
    ...collective.map((c): Listed => {
      if (kind === "ocu") {
        const link = ccLinksOf(c.ocuIds)[String(item.id)] ?? null;
        return { key: "cc" + String(c.id), matter: c, isCC: true, party: partyOfCounsel(link), link };
      }
      // a collective case keeps its contacts as ids alone (D5): no per-matter state there — acting
      return { key: "cc" + String(c.id), matter: c, isCC: true, party: clientName, link: null };
    }),
  ];
  // the acting ones first: the rows numbered, their links in a map the shared ordering reads
  const byIndex = new Map(listed.map((l, i) => [i, l]));
  const linksByIndex: Record<string, LinkState | null> = {};
  listed.forEach((l, i) => {
    linksByIndex[String(i)] = l.link;
  });
  const ordered = actingFirst(listed.map((_l, i) => i), linksByIndex)
    .map((i) => byIndex.get(i))
    .filter((l): l is Listed => !!l);

  const open = (l: Listed) => {
    nav(l.isCC ? "cc" : "ca", l.matter.id);
  };

  return (
    <div className="detail-card" data-party-matters={kind}>
      <h4>{t("list.section.cases", { fallback: "Cases" })}</h4>
      {ordered.length === 0 ? (
        <span style={{ color: "var(--tm)", fontSize: FS.s9 }}>{t("common.none", { fallback: "None" })}</span>
      ) : (
        ordered.map((l) => {
          const c = l.matter;
          const name = [c.ref, c.title].filter(Boolean).join(" · ");
          const suspended = isSuspended(l.link);
          const since = shortDate(suspendedDateOf(l.link));
          return (
            <div
              key={l.key}
              role="button"
              tabIndex={0}
              data-party-matter={l.key}
              data-link-state={suspended ? "suspended" : "acting"}
              onClick={() => {
                open(l);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") open(l);
              }}
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0", borderBottom: "1px solid var(--bd)", cursor: "pointer", opacity: c.archived || suspended ? 0.62 : 1 }}
            >
              <span className="name-cell" style={{ flex: "1 1 auto", fontSize: FS.s11 }} title={name || undefined}>
                {name || "—"}
                {c.archived && <span style={{ color: "var(--tm)", fontSize: FS.s9 }}>{" · " + t("client.record.archived_matter", { fallback: "archived" })}</span>}
              </span>
              {l.party && (
                <span style={{ fontSize: FS.s9, color: "var(--tm)", whiteSpace: "nowrap" }} title={t("record.party_represented", { fallback: "Party represented" })}>
                  {l.party}
                </span>
              )}
              <span style={{ fontSize: FS.s9, color: "var(--tm)", fontStyle: suspended ? "italic" : "normal", whiteSpace: "nowrap" }}>
                {suspended ? t("link.suspended_since", { date: since, fallback: `suspended since ${since}` }) : t("link.acting", { fallback: "acting" })}
              </span>
            </div>
          );
        })
      )}
    </div>
  );
}
